import deWeb from '../locales/de/web.json';
import enWeb from '../locales/en/web.json';

/**
 * The tenant invite mail ("Mieter zur App einladen") from ONE text source: the web dialog shows exactly this as its
 * preview and the server sends exactly this (apps/server/src/modules/account.ts). Before 04.11.2026 the server had its
 * own text ("<Vermieter> lädt Sie zu ImmoAktas ein", only code and button) and the preview promised something else.
 * The texts live in web.json `tenantInvite.mail` (de/en parity checked); `_du` variants follow the account's Anrede.
 */
export type InviteMailForm = 'du' | 'sie';
export type InviteMailVars = { landlord: string; property: string; name: string; code: string; days: number };
export type TenantInviteMail = { fromName: string; subject: string; hello: string; text: string; code: string; button: string; note: string };

const MAIL = { de: deWeb.tenantInvite.mail, en: enWeb.tenantInvite.mail } as const;
type MailKey = keyof typeof deWeb.tenantInvite.mail;

const fill = (s: string, v: Record<string, string | number>) => s.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) => String(v[k] ?? ''));

/** Name for "Hallo …": the first name, but the whole name for households and salutations ("Familie Nowak", "Herr Kaya"). */
const SALUTATIONS = new Set(['familie', 'fam.', 'family', 'herr', 'frau', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'dr.', 'dr']);
export const greetingName = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 && SALUTATIONS.has(parts[0]!.toLowerCase()) ? parts.join(' ') : (parts[0] ?? '');
};

/** "im Auftrag von …": the letterhead name the tenants know from their letters, else the account name. */
export const inviteLandlordName = (a: { name?: string | null; letterhead?: { name?: string | null } | null } | null | undefined) => a?.letterhead?.name || a?.name || '';

/** `name` is the person's full name; the greeting uses greetingName(). Unknown language → German. */
export function tenantInviteMail(lang: string, form: InviteMailForm, v: InviteMailVars): TenantInviteMail {
  const m = MAIL[lang === 'en' ? 'en' : 'de'] as Record<string, string>;
  const vars = { ...v, name: greetingName(v.name) };
  const pick = (k: MailKey) => fill((form === 'du' ? m[`${k}_du`] : undefined) ?? m[k]!, vars);
  return { fromName: pick('fromValue'), subject: pick('subjectValue'), hello: pick('hello'), text: pick('text'), code: pick('code'), button: pick('button'), note: pick('note') };
}
